import { Avatar, Box, CircularProgress, Typography } from "@mui/material";
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import GridMediaCard from "../../components/cards/GridMediaCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageHero from "../../components/motion/PageHero";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import { useConstants } from "../../hooks/UseConstants";
import { formatCurrency } from "../../helper/formatCurrency";

function parseList(result) {
    if (result?.status !== 200) return [];
    const payload = result.data?.data;
    return Array.isArray(payload) ? payload : (payload?.data || []);
}

function TeacherProfile() {
    const { host, language } = useConstants();
    const { user } = useContext(AuthContext);
    const isAr = language === 'ar';
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [teacher, setTeacher] = useState(location.state?.teacher ?? null);
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            let url = `${host}/courses?teacher_id=${id}&per_page=50&status[]=accepted`;
            if (user?.major?.id) url += '&for_user=1';
            const result = await Fetch(url, 'GET');
            const list = parseList(result);
            setCourses(list);
            if (list[0]?.teacher) {
                setTeacher(list[0].teacher);
            } else if (location.state?.teacher) {
                setTeacher(location.state.teacher);
            }
            setLoading(false);
        };
        load();
    }, [host, id, user?.major?.id, location.state?.teacher]);

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress size={70} /></Box>;

    if (!teacher && courses.length === 0) {
        return (
            <Typography color="text.secondary" className="text-center py-16">
                {isAr ? 'لا توجد بيانات لهذا المدرس' : 'No data for this teacher'}
            </Typography>
        );
    }

    const name = teacher ? `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() : (isAr ? 'مدرس' : 'Teacher');
    const subtitle = teacher?.specialization
        ? (teacher.specialization.name_ar || teacher.specialization.name_en)
        : teacher?.bio?.slice(0, 80);

    return (
        <Box>
            <PageHero
                title={name}
                subtitle={subtitle}
                icon={
                    teacher?.image ? (
                        <Avatar src={teacher.image} sx={{ width: 64, height: 64, border: '3px solid rgba(255,255,255,0.5)' }} />
                    ) : (
                        <PersonOutlinedIcon />
                    )
                }
            />
            <DashboardPanelCard title={isAr ? 'دورات المدرس' : 'Teacher courses'} className="mx-0 mb-4">
            {courses.length === 0 ? (
                <Typography color="text.secondary">{isAr ? 'لا توجد دورات متاحة' : 'No courses available'}</Typography>
            ) : (
                <Box className="grid grid-cols-3 gap-5 max-sm:grid-cols-1 justify-items-center">
                    {courses.map((course, i) => (
                        <ScrollReveal key={course.id} delay={i * 60} direction="up" className="w-full">
                            <GridMediaCard
                                image={resolveCourseImage(course)}
                                title={course.name_ar || course.name_en}
                                onClick={() => navigate(`/courses/${course.id}`)}
                                footer={
                                    <Typography variant="caption" className="text-blue-600 font-bold">
                                        {formatCurrency(course.effective_price ?? course.price, isAr)}
                                    </Typography>
                                }
                            />
                        </ScrollReveal>
                    ))}
                </Box>
            )}
            </DashboardPanelCard>
        </Box>
    );
}

export default TeacherProfile;
